import { Box, Typography, ListItem, ListItemText, Button, Modal, IconButton, CircularProgress } from "@mui/material";
import { useState } from "react";
import theme from "../styles/theme";
import { resolveMediaUrl } from "../api";
import { Bar } from "react-chartjs-2";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";

interface EvaluationItemProps {
  evaluation: any;
  exerciseTitle: string;
  totalScore: number;
  maxPoints: number;
  scores: number[];
  averageScore: string | number;
  successRate: number | string;
  regularity: string;
  resolvedVideos: any[];
  barData: any;
  barOptions: any;
}

export default function EvaluationItem({
  evaluation,
  exerciseTitle,
  totalScore,
  maxPoints,
  scores,
  averageScore,
  successRate,
  regularity,
  resolvedVideos,
  barData,
  barOptions,
}: EvaluationItemProps) {
  // Fonction pour choisir la couleur selon le taux
  const getRateColor = (rate: number) => {
    if (rate < 30) return theme.palette.error.main; // rouge
    if (rate < 50) return theme.palette.warning.main; // orange
    if (rate < 70) return theme.palette.info.main; // jaune/bleu clair (adapter si besoin)
    if (rate < 90) return theme.palette.success.light; // vert clair
    return theme.palette.success.main; // vert foncé
  };
  // Modale lecture vidéo
  const [videoModalOpen, setVideoModalOpen] = useState(false);
  const [currentVideo, setCurrentVideo] = useState<any | null>(null);
  const [videoLoading, setVideoLoading] = useState(false);
  const [videoError, setVideoError] = useState<string | null>(null);
  const openVideo = (video: any) => {
    const id = getYouTubeId(video.url || "");
    setCurrentVideo(video);
    setVideoError(null);
    setVideoLoading(!id); // on affiche le loading pour les vidéos non-YouTube
    setVideoModalOpen(true);
  };
  const closeVideo = () => {
    setVideoModalOpen(false);
    setCurrentVideo(null);
    setVideoLoading(false);
    setVideoError(null);
  };
  const getYouTubeId = (url: string) => {
    const m = url.match(/(?:\?v=|\.be\/|\/embed\/|\/shorts\/)([\w-]+)/);
    return m ? m[1] : null;
  };
  const [currentIdx, setCurrentIdx] = useState(0);
  const [animKey, setAnimKey] = useState(0);
  const [directionSlide, setDirectionSlide] = useState<"left" | "right">("left");
  const [showVideos, setShowVideos] = useState(false);
  const handlePrevCard = (len: number) => {
    setDirectionSlide("right");
    setCurrentIdx((prev: number) => (prev === 0 ? len - 1 : prev - 1));
    setAnimKey((k: number) => k + 1);
  };
  const handleNextCard = (len: number) => {
    setDirectionSlide("left");
    setCurrentIdx((prev: number) => (prev === len - 1 ? 0 : prev + 1));
    setAnimKey((k: number) => k + 1);
  };

  const renderVideoCarousel = () => (
    <>
      <Typography
        variant="h6"
        sx={{ fontWeight: 'bold', mb: 1, color: theme.palette.warning.main, fontFamily: theme.typography.fontFamily }}
      >
        Comment progresser sur cet exercice ?
      </Typography>
      {/* Carousel de cards avec animation glissée */}
      <Box sx={{ width: '100%', maxWidth: 560, mx: 'auto' }}>
        {(() => {
          const v = resolvedVideos[currentIdx];
          return (
            <Box sx={{ position: 'relative' }}>
              <Box
                key={animKey}
                className={directionSlide === 'left' ? 'card-slide-left' : 'card-slide-right'}
                sx={{
                  border: `1px solid ${theme.palette.secondary.light}`,
                  borderRadius: theme.shape.borderRadius,
                  overflow: 'hidden',
                  boxShadow: '0 1px 6px rgba(0,0,0,0.08)',
                  backgroundColor: theme.palette.background.paperDark,
                  padding: 10,
                  color: theme.palette.text.primary,
                }}
              >
                <Box sx={{ p: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1 }}>
                    <PlayCircleOutlineIcon sx={{ fontSize: 32, color: theme.palette.primary.contrastText }} />
                  </Box>
                  <Typography
                    variant="subtitle2"
                    sx={{ fontWeight: 700, mb: 0.75, fontSize: 22, textAlign: 'center', color: theme.palette.primary.contrastText }}
                  >
                    {v.title}
                  </Typography>
                  {v.description && (
                    <Typography
                      variant="body2"
                      sx={{ color: theme.palette.primary.contrastText, fontSize: 17, mb: 1.5, textAlign: 'center' }}
                    >
                      {v.description}
                    </Typography>
                  )}
                  <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                    <Button
                      variant="contained"
                      size="small"
                      sx={{ fontWeight: 'bold', mb: 1, backgroundColor: theme.palette.warning.main, color: '#000', border: `1px solid ${theme.palette.warning.light}` }}
                      onClick={() => openVideo(v)}
                    >
                      Voir l'exercice en vidéo
                    </Button>
                  </Box>
                </Box>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mt: 2 }}>
                <IconButton onClick={() => handlePrevCard(resolvedVideos.length)} aria-label="Vidéo précédente">
                  <ArrowBackIosIcon />
                </IconButton>
                <Typography variant="body2" component="span" sx={{ mx: 2 }}>
                  {currentIdx + 1} / {resolvedVideos.length}
                </Typography>
                <IconButton onClick={() => handleNextCard(resolvedVideos.length)} aria-label="Vidéo suivante">
                  <ArrowForwardIosIcon />
                </IconButton>
              </Box>
            </Box>
          );
        })()}
      </Box>
      <style>{`
        @keyframes cardSlideInLeft {
          from { transform: translateX(100%); opacity: 0.9; }
          to { transform: translateX(0); opacity: 1; }
        }
        @keyframes cardSlideInRight {
          from { transform: translateX(-100%); opacity: 0.9; }
          to { transform: translateX(0); opacity: 1; }
        }
        .card-slide-left { animation: cardSlideInLeft 350ms ease; will-change: transform; }
        .card-slide-right { animation: cardSlideInRight 350ms ease; will-change: transform; }
      `}</style>
    </>
  );

  const rateValue = typeof successRate === 'number' ? successRate : parseFloat(String(successRate));

  return (
    <ListItem
      key={evaluation.id}
      sx={{
        marginBottom: 2,
        border: `1px solid ${theme.palette.secondary.main}`,
        borderRadius: theme.shape.borderRadius,
        padding: { xs: 1, sm: 2 },
        backgroundColor: theme.palette.background.default,
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        justifyContent: 'space-between',
        alignItems: { xs: 'stretch', md: 'flex-start' },
        position: 'relative',
        width: '100%',
        boxShadow: '0 1px 6px rgba(0,0,0,0.04)',
      }}
    >
      {rateValue >= 55 && (
        <Box
          sx={{
            position: { xs: 'static', md: 'absolute' },
            top: { md: 12 },
            right: { md: 24 },
            zIndex: 2,
            alignSelf: { xs: 'flex-end', md: 'auto' },
            mb: { xs: 1, md: 0 },
          }}
        >
          <Typography
            variant="h6"
            sx={{
              fontWeight: 'bold',
              backgroundColor: getRateColor(rateValue),
              fontFamily: theme.typography.fontFamily,
              fontSize: { xs: '1rem', sm: '1.2rem' },
              color: theme.palette.common.white,
              px: 2,
              py: 0.5,
              borderRadius: theme.shape.borderRadius,
              boxShadow: '0 1px 6px rgba(0,0,0,0.07)',
              textAlign: 'center',
            }}
          >
            Taux de réussite : {typeof successRate === 'number' ? `${successRate}%` : successRate}
          </Typography>
        </Box>
      )}
      <Box sx={{ flex: 1, marginRight: { xs: 0, md: 2 }, width: { xs: '100%', md: '60%' } }}>
        <ListItemText
          primary={
            <Typography variant="h5" sx={{ fontWeight: 'bold', color: theme.palette.primary.main, fontFamily: theme.typography.fontFamily }}>
              {exerciseTitle}
            </Typography>
          }
          secondary={
            <>
              <Typography variant="body1" sx={{ marginBottom: 1, color: theme.palette.text.primary }}>
                <strong>Note totale :</strong> {totalScore}/{maxPoints * (scores.length || 1)}
              </Typography>
              <Typography variant="body1" sx={{ marginBottom: 1, color: theme.palette.text.primary }}>
                <strong>Notes individuelles :</strong> {scores.join(', ')}
              </Typography>
              <Typography variant="body1" sx={{ marginBottom: 1, color: theme.palette.text.primary }}>
                <strong>Moyenne des notes :</strong> {averageScore}
              </Typography>
              <Typography variant="body1" sx={{ marginBottom: 1, color: theme.palette.text.primary }}>
                <strong>Régularité :</strong> {regularity}
              </Typography>
              {resolvedVideos.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  {rateValue < 75 ? (
                    renderVideoCarousel()
                  ) : (
                    <>
                      <Box sx={{ bgcolor: theme.palette.background.paper, padding: 2, borderRadius: theme.shape.borderRadius, mt: 1.5 }}>
                        <Typography
                          variant="body1"
                          sx={{ color: theme.palette.secondary.main, mb: 1, fontWeight: 'bold' }}
                        >
                          Tu as obtenu un bon score sur cet exercice !
                        </Typography>
                        <Typography variant="body2" sx={{ color: theme.palette.text.secondary, mb: 1.5 }}>
                          Si tu veux encore progresser et te perfectionner, tu peux aussi regarder les vidéos associées.
                        </Typography>
                        <Button
                          variant="contained"
                          size="small"
                          sx={{ bgcolor: theme.palette.secondary.main, mb: 1, fontWeight: 'bold' }}
                          onClick={() => setShowVideos(true)}
                        >
                          Voir les vidéos de l'exercice
                        </Button>
                      </Box>
                      {showVideos && (
                        <Box sx={{ mt: 2 }}>
                          {renderVideoCarousel()}
                        </Box>
                      )}
                    </>
                  )}
                </Box>
              )}
            </>
          }
        />
      </Box>
      <Box sx={{ width: { xs: '100%', md: '40%' }, paddingTop: { xs: 2, md: '50px' } }}>
        <Bar data={barData} options={barOptions} />
      </Box>
      <style>{`
        @media (max-width: 900px) {
          .MuiTypography-h6 {
            font-size: 1rem !important;
          }
        }
        @media (max-width: 600px) {
          .MuiTypography-h6 {
            font-size: 0.9rem !important;
          }
        }
      `}</style>

      {/* Modale vidéo plein écran */}
      <Modal open={videoModalOpen} onClose={closeVideo} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Box sx={{ width: '90vw', height: '90vh', backgroundColor: theme.palette.background.paper, borderRadius: theme.shape.borderRadius, boxShadow: '0 2px 24px rgba(0,0,0,0.12)', overflow: 'hidden', position: 'relative' }}>
          <Button onClick={closeVideo} variant="contained" color="error" sx={{ position: 'absolute', top: 12, right: 12, zIndex: 2 }}>Fermer</Button>
          {currentVideo && (() => {
            const rawUrl = currentVideo.url || "";
            const url = resolveMediaUrl(rawUrl);
            if (!url) {
              return <Box sx={{ p: 3 }}><Typography>URL de vidéo non reconnue.</Typography></Box>;
            }

            const id = getYouTubeId(url);
            if (id) {
              return (
                <Box sx={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}>
                  <iframe
                    src={`https://www.youtube.com/embed/${id}`}
                    title={currentVideo.title}
                    frameBorder={0}
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    referrerPolicy="strict-origin-when-cross-origin"
                    allowFullScreen
                    style={{ width: '100%', height: '100%' }}
                  />
                </Box>
              );
            }

            // Pour toute URL non-YouTube, tenter la lecture via <video>
            return (
              <Box sx={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', bgcolor: 'black' }}>
                {videoLoading && !videoError && (
                  <Box
                    sx={{
                      position: 'absolute',
                      inset: 0,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: 'rgba(0,0,0,0.4)',
                      zIndex: 2,
                    }}
                  >
                    <CircularProgress color="inherit" />
                  </Box>
                )}
                <video
                  src={url}
                  controls
                  autoPlay
                  onLoadedData={() => setVideoLoading(false)}
                  onError={() => {
                    // Log de l'URL exacte qui échoue pour aider au debug
                    console.error("Erreur de chargement vidéo, URL :", url);
                    setVideoLoading(false);
                    setVideoError("Impossible de charger la vidéo.");
                  }}
                  style={{ width: '100%', height: '100%' }}
                >
                  Votre navigateur ne supporte pas la lecture vidéo.
                </video>
                {videoError && (
                  <Box sx={{ position: 'absolute', bottom: 16, left: 0, width: '100%', textAlign: 'center' }}>
                    <Typography variant="body2" sx={{ color: '#fff', px: 2 }}>
                      {videoError}
                    </Typography>
                  </Box>
                )}
              </Box>
            );
          })()}
        </Box>
      </Modal>
    </ListItem>
  );
}
